import { useState } from "react";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";

const STAGES = ["Identified", "Accepted", "Actioned", "Verified", "Persisting"];
const OUTCOMES = ["verified", "partially verified", "not verified", "inconclusive", "adverse"];

export default function Actions() {
  const opps = usePolling(() => api.get("/opportunities").then((r) => r.data), 8000);
  const actionsPoll = usePolling(() => api.get("/actions").then((r) => r.data), 5000);
  const [dialogAction, setDialogAction] = useState(null);
  const [rationale, setRationale] = useState("");
  const [baseline, setBaseline] = useState("");
  const [outcome, setOutcome] = useState("");
  const [formError, setFormError] = useState("");
  const { canWrite } = useAuth();

  const createAction = async (opp) => {
    await api.post("/actions", { opportunity_id: opp.opp_id });
  };

  const nextStage = dialogAction ? STAGES[STAGES.indexOf(dialogAction.status) + 1] : null;

  const advance = async () => {
    setFormError("");
    try {
      await api.patch(`/actions/${dialogAction.action_id}/transition`, {
        status: nextStage, rationale: rationale || undefined,
        baseline_value: baseline ? Number(baseline) : undefined,
        verification_outcome: outcome || undefined,
      });
      setDialogAction(null);
      setRationale("");
      setBaseline("");
      setOutcome("");
    } catch (e) {
      setFormError(e?.response?.data?.detail || "Could not advance action.");
    }
  };

  return (
    <div className="space-y-6" data-testid="actions-page">
      <h1 className="text-2xl font-bold font-heading text-[var(--oim-text)]">Actions & M&V</h1>
      <ReadOnlyNotice>Creating and progressing actions is disabled for read-only accounts.</ReadOnlyNotice>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
        <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Opportunities</div>
        {!opps.data ? (
          <div className="text-sm text-[var(--oim-text-muted)]" data-testid="actions-opportunities-loading">Loading…</div>
        ) : opps.data.length === 0 ? (
          <div className="text-sm text-[var(--oim-text-muted)]" data-testid="actions-opportunities-empty">No opportunities identified yet.</div>
        ) : (
          <div className="space-y-2">
            {opps.data.map((o) => (
              <div key={o.opp_id} className="flex items-center justify-between text-xs" data-testid={`opportunity-row-${o.opp_id}`}>
                <span className="text-[var(--oim-text-secondary)]">{o.title} — {STAGES[o.stage]}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono-data text-[var(--oim-text)]">₹{Math.round(o.value_identified).toLocaleString("en-IN")}/day</span>
                  <Button size="sm" className="bg-[#00C853] text-black hover:bg-[#00a844] rounded-full text-xs" onClick={() => createAction(o)} disabled={!canWrite} title={canWrite ? undefined : "Read-only account"} data-testid={`create-action-from-${o.opp_id}-button`}>
                    Create action
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
        <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Actions</div>
        {!actionsPoll.data ? (
          <div className="text-sm text-[var(--oim-text-muted)]" data-testid="actions-list-loading">Loading…</div>
        ) : actionsPoll.data.length === 0 ? (
          <div className="text-sm text-[var(--oim-text-muted)]" data-testid="actions-list-empty">No actions created yet.</div>
        ) : (
          <div className="space-y-2">
            {actionsPoll.data.map((a) => (
              <div key={a.action_id} className="flex items-center justify-between text-xs" data-testid={`action-row-${a.action_id}`}>
                <span className="text-[var(--oim-text-secondary)]">
                  <span className="font-heading">{a.title || a.action_id}</span>
                  {a.source === "askniq" && (
                    <span className="text-[8px] font-heading uppercase px-1.5 py-0.5 rounded-full border ml-2" style={{ borderColor: "var(--oim-green)", color: "var(--oim-green)" }} data-testid={`action-source-askniq-${a.action_id}`}>
                      Ask Niq
                    </span>
                  )}
                  <span className="text-[var(--oim-text-dim)] ml-2">— {a.status}</span>
                </span>
                <Button size="sm" variant="outline" className="border-[var(--oim-border)] text-[var(--oim-text)] text-xs" onClick={() => setDialogAction(a)} disabled={!canWrite} title={canWrite ? undefined : "Read-only account"} data-testid={`verify-action-${a.action_id}-button`}>
                  Progress
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!dialogAction} onOpenChange={(o) => !o && setDialogAction(null)}>
        <DialogContent className="bg-[var(--oim-surface)] border-[var(--oim-border)] text-[var(--oim-text)]">
          <DialogHeader><DialogTitle>Progress action {dialogAction?.action_id}</DialogTitle></DialogHeader>
          {!nextStage ? (
            <div className="text-sm text-[var(--oim-text-muted)]" data-testid="action-no-next-stage">This action has completed its lifecycle (Persisting).</div>
          ) : (
            <>
              <div className="text-xs text-[var(--oim-text-muted)]">Next stage: <span className="font-heading font-semibold text-[var(--oim-text)]">{nextStage}</span></div>
              <div className="space-y-3">
                {(nextStage === "Actioned" || nextStage === "Verified" || nextStage === "Persisting") && (
                  <div>
                    <label className="text-[10px] uppercase text-[var(--oim-text-dim)] block mb-1">Baseline value (required before Actioned)</label>
                    <input value={baseline} onChange={(e) => setBaseline(e.target.value)} className="w-full bg-transparent border border-[var(--oim-border)] rounded px-2 py-1 text-sm" data-testid="action-baseline-input" />
                  </div>
                )}
                {nextStage === "Verified" && (
                  <div>
                    <label className="text-[10px] uppercase text-[var(--oim-text-dim)] block mb-1">Verification outcome (required)</label>
                    <Select value={outcome} onValueChange={setOutcome}>
                      <SelectTrigger data-testid="action-verification-outcome-select" className="bg-transparent border-[var(--oim-border)]">
                        <SelectValue placeholder="Select outcome" />
                      </SelectTrigger>
                      <SelectContent>
                        {OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase text-[var(--oim-text-dim)] block mb-1">Rationale</label>
                  <Textarea value={rationale} onChange={(e) => setRationale(e.target.value)} data-testid="action-rationale-input" className="bg-transparent border-[var(--oim-border)]" />
                </div>
                {formError && <div className="text-xs text-red-400" data-testid="action-transition-error">{formError}</div>}
              </div>
              <DialogFooter>
                <Button
                  size="sm" disabled={!rationale}
                  onClick={advance}
                  data-testid={`transition-${nextStage.toLowerCase()}-button`}
                  className="bg-[#00C853] text-black hover:bg-[#00a844] text-xs"
                >
                  Advance to {nextStage}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
